import { useRef, type MutableRefObject } from 'react';
import {
	autoUpdate,
	offset,
	Placement,
	shift,
	useDismiss,
	useFloating,
	useInteractions,
	size,
	useListNavigation,
	useRole,
	autoPlacement,
} from '@floating-ui/react';

type UseSelectFloatingReturn = {
	listRef: React.RefObject<Array<HTMLElement | null>>;
	dismiss: ReturnType<typeof useDismiss>;
	role: ReturnType<typeof useRole>;
	refs: ReturnType<typeof useFloating>['refs'];
	floatingStyles: React.CSSProperties;
	context: ReturnType<typeof useFloating>['context'];
	getFloatingProps: ReturnType<typeof useInteractions>['getFloatingProps'];
	getReferenceProps: ReturnType<typeof useInteractions>['getReferenceProps'];
	getItemProps: ReturnType<typeof useInteractions>['getItemProps'];
	fieldRef: React.RefObject<HTMLDivElement | null>;
};

export const useSelectFloating = ({
	open,
	onOpenChange,
	highlightedIndex,
	setHighlightedIndex,
	interactionModeRef,
	placement = 'bottom',
	dontOpenDropdownIfNoOptions,
	options,
	textInputValue,
	strategy = 'absolute',
	autoplacement = true,
	ignoreSize = false,
}: {
	open: boolean;
	onOpenChange: (v: boolean) => void;
	highlightedIndex: number;
	setHighlightedIndex: (i: number) => void;
	interactionModeRef: MutableRefObject<'keyboard' | 'pointer'>;
	placement?: Placement;
	dontOpenDropdownIfNoOptions?: boolean;
	options: any[];
	textInputValue: string;
	strategy?: 'absolute' | 'fixed';
	autoplacement?: boolean;
	ignoreSize?: boolean;
}): UseSelectFloatingReturn => {
	const listRef = useRef<Array<HTMLElement | null>>([]);
	const fieldRef = useRef<HTMLDivElement | null>(null);

	const { refs, floatingStyles, context } = useFloating({
		strategy: strategy,
		placement: placement,
		middleware: [
			autoplacement
				? autoPlacement({
						allowedPlacements: ['bottom-start', 'top-start', 'bottom-end', 'top-end'],
					})
				: null,
			shift(),
			offset(8),
			ignoreSize
				? null
				: size({
						apply({ rects, elements, availableHeight }) {
							const w = Math.round(fieldRef.current?.offsetWidth ?? rects.reference.width);
							Object.assign(elements.floating.style, {
								width: `${w}px`,
								maxHeight: `${availableHeight}px`,
								boxSizing: 'border-box',
							});
						},
					}),
		],
		whileElementsMounted: autoUpdate,
		open: open,
		onOpenChange: (isOpen) => {
			const isEmptyQuery = !textInputValue || textInputValue.trim() === '';

			if (dontOpenDropdownIfNoOptions && isEmptyQuery && options.length === 0 && isOpen) {
				return;
			}

			onOpenChange(isOpen);
		},
	});

	const dismiss = useDismiss(context, { outsidePress: true, escapeKey: true });
	const role = useRole(context, { role: 'listbox' });

	const listNav = useListNavigation(context, {
		listRef,
		activeIndex: highlightedIndex,
		onNavigate: (activeIndex: number | null) => {
			interactionModeRef.current = 'keyboard';
			setHighlightedIndex(activeIndex ?? 0);
		},
		loop: true,
		virtual: true,
		focusItemOnOpen: false,
	});

	const { getFloatingProps, getReferenceProps, getItemProps } = useInteractions([
		dismiss,
		role,
		listNav,
	]);

	return {
		listRef,
		dismiss,
		role,
		refs,
		floatingStyles,
		context,
		getFloatingProps,
		getReferenceProps,
		getItemProps,
		fieldRef,
	};
};
